import Clarity from "@microsoft/clarity";
import { FC, useCallback, useContext, useEffect } from "react";
import { clarityProjectId } from "~config";
import { BaseContext } from "~context";
import { ENABLE_TRACKING_SCRIPTS } from "~features/tracking/config";
import { useAccount } from "~hooks";

export const ClarityScript: FC = () => {
  const { user } = useAccount();
  const { organization } = useContext(BaseContext);

  useEffect(() => {
    if (!shouldLoadClarity()) {
      return;
    }
    Clarity.init(clarityProjectId);
    Clarity.consent();
  }, []);

  useEffect(() => {
    if (!isClarityLoaded() || !organization?.id) return;

    Clarity.setTag("orgId", organization.id);
  }, [organization?.id]);

  useEffect(() => {
    if (!isClarityLoaded() || !user) {
      return;
    }

    Clarity.identify(user.id);
  }, [user]);

  return null;
};

/**
 * Returns a stable helper to attach custom key/value tags to the Clarity session.
 *
 * @example
 * const setTag = useClarityTag();
 * setTag("plan", "premium");
 * setTag("org", organisationId);
 */
export const useClarityTag = () => {
  return useCallback((key: string, value: string | string[]) => {
    if (!isClarityLoaded()) return;
    Clarity.setTag(key, value);
  }, []);
};

function shouldLoadClarity() {
  return (
    typeof window !== "undefined" &&
    ENABLE_TRACKING_SCRIPTS &&
    !!clarityProjectId
  );
}

function isClarityLoaded() {
  return typeof window !== "undefined" && typeof window.clarity === "function";
}
